import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { ASSETS, COMPANY, SERVICES } from "@/lib/data";
import { Button } from "@/components/ui/button";

const serviceLinks = SERVICES.map((s) => ({ name: s.title, path: s.path }));

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const isActive = (path) => location.pathname === path;
  const tjansterActive = location.pathname.startsWith("/tjanster") || location.pathname === "/golvvard";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all ${
        scrolled ? "bg-white/90 backdrop-blur-xl shadow-sm" : "bg-white/70 backdrop-blur-md"
      } border-b border-slate-100`}
    >
      {/* Top contact strip */}
      <div className="hidden lg:block bg-[#1E3A6F] text-white/90 text-xs">
        <div className="max-w-7xl mx-auto px-6 py-2 flex items-center justify-between">
          <span data-testid="top-strip-area">Vi arbetar dagligen i hela nordvästra Skåne</span>
          <div className="flex items-center gap-6">
            <a href={`mailto:${COMPANY.email}`} className="hover:text-[#F59E0B] transition-colors" data-testid="top-strip-email">{COMPANY.email}</a>
            <a href={COMPANY.phoneLink} className="hover:text-[#F59E0B] transition-colors flex items-center gap-1.5" data-testid="top-strip-phone">
              <Phone className="h-3.5 w-3.5" /> {COMPANY.phone}
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3" data-testid="header-logo" aria-label="Rena Tjänster – startsida">
            <img src={ASSETS.logo} alt="Rena Tjänster logotyp" className="h-16 w-auto rounded-md" />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-8" aria-label="Huvudmeny">
            {/* Tjänster dropdown */}
            <div
              className="relative"
              onMouseEnter={() => setOpenDropdown("tjanster")}
              onMouseLeave={() => setOpenDropdown(null)}
            >
              <Link
                to="/tjanster"
                className={`nav-link flex items-center gap-1 ${tjansterActive ? "active" : ""}`}
                data-testid="nav-tjanster"
              >
                Tjänster <ChevronDown className="h-4 w-4" />
              </Link>
              {openDropdown === "tjanster" && (
                <div className="dropdown-panel absolute top-full left-0 pt-3" data-testid="dropdown-tjanster">
                  <div className="bg-white rounded-2xl shadow-xl border border-slate-100 py-3 w-72">
                    {serviceLinks.map((l) => (
                      <Link
                        key={l.path}
                        to={l.path}
                        className="block px-5 py-2.5 text-sm text-slate-700 hover:text-[#1E3A6F] hover:bg-slate-50 transition-colors"
                        data-testid={`nav-tjanster-${l.path}`}
                      >
                        {l.name}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/om-oss" className={`nav-link ${isActive("/om-oss") ? "active" : ""}`} data-testid="nav-om-oss">
              Om företaget
            </Link>
            <Link to="/miljo-och-hallbarhet" className={`nav-link ${isActive("/miljo-och-hallbarhet") ? "active" : ""}`} data-testid="nav-miljo">
              Miljö & hållbarhet
            </Link>
            <Link to="/kontakt" className={`nav-link ${isActive("/kontakt") ? "active" : ""}`} data-testid="nav-kontakt">
              Kontakt
            </Link>
          </nav>

          {/* CTA + mobile button */}
          <div className="flex items-center gap-3">
            <Link to="/kontakt#offertformular" data-testid="header-cta-offert">
              <Button
                className="hidden sm:inline-flex bg-[#1E3A6F] hover:bg-[#152a51] text-white rounded-full px-6 py-5 font-medium transition-all hover:-translate-y-0.5"
              >
                Begär offert
              </Button>
            </Link>
            <button
              className="lg:hidden p-2 rounded-md hover:bg-slate-100"
              onClick={() => setMobileOpen((s) => !s)}
              aria-label="Öppna meny"
              data-testid="mobile-menu-toggle"
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <nav className="lg:hidden border-t border-slate-100 bg-white" data-testid="mobile-nav" aria-label="Mobilmeny">
          <div className="max-w-7xl mx-auto px-4 py-6 space-y-1">
            <MobileGroup title="Tjänster" links={serviceLinks} basePath="/tjanster" testIdPrefix="mob-tjanster" />
            <Link to="/om-oss" className="block py-3 px-2 font-medium text-slate-900 border-b border-slate-100" data-testid="mob-om-oss">Om företaget</Link>
            <Link to="/miljo-och-hallbarhet" className="block py-3 px-2 font-medium text-slate-900 border-b border-slate-100" data-testid="mob-miljo">Miljö & hållbarhet</Link>
            <Link to="/kontakt" className="block py-3 px-2 font-medium text-slate-900 border-b border-slate-100" data-testid="mob-kontakt">Kontakt</Link>
            <Link to="/kontakt#offertformular" data-testid="mob-cta-offert" className="block mt-4">
              <Button
                className="w-full bg-[#1E3A6F] hover:bg-[#152a51] text-white rounded-full py-6"
              >
                Begär offert
              </Button>
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}

function MobileGroup({ title, links, basePath, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-100">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-3 px-2 font-medium text-slate-900"
        data-testid={`${testIdPrefix}-toggle`}
      >
        {title}
        <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pl-4 pb-2 space-y-1">
          <Link to={basePath} className="block py-2 text-sm text-[#1E3A6F]" data-testid={`${testIdPrefix}-overview`}>
            Översikt
          </Link>
          {links.map((l) => (
            <Link key={l.path} to={l.path} className="block py-2 text-sm text-slate-600" data-testid={`${testIdPrefix}-${l.path}`}>
              {l.name}
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
